"use client";

import { useEffect, useMemo, useState } from "react";
import { resolvePath, resolveText } from "./binding";
import { actionNameFromConfig, resolveActionContext } from "./helpers";
import { arraysEqual, buildRollPickerSnapshot, parseRollPickerColumns, resolveRollPickerDisplayIndexes, resolveRollPickerRawSelection, RollPickerFrame } from "./rollPickerShared";
import { MACARON } from "./tokens";
import type { MacaronRenderContext, RecordAny } from "./types";

const DEFAULT_ROLL_PICKER_CARD_TITLE = "Ready to spin...";
const DEFAULT_ROLL_PICKER_CARD_SUBTITLE = "Tap Spin to reveal your picks";
const DEFAULT_ROLL_PICKER_CARD_SUBMIT_TEXT = "Spin";
const DEFAULT_ROLL_PICKER_CARD_SUBMITTED_TEXT = "Search nearby results";

export function MacaronRollPickerCard({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const columns = useMemo(() => parseRollPickerColumns(config.columns, ctx.data), [config.columns, ctx.data]);
  const externalSelection = useMemo(() => resolveRollPickerRawSelection(config.selection, ctx.data), [config.selection, ctx.data]);
  const controlledIndexes = useMemo(() => resolveRollPickerDisplayIndexes(columns, externalSelection), [columns, externalSelection]);
  const controlledSnapshot = useMemo(() => buildRollPickerSnapshot(columns, controlledIndexes), [columns, controlledIndexes]);
  const [indexes, setIndexes] = useState<number[]>(() => controlledIndexes);
  const [hasSubmittedSelection, setHasSubmittedSelection] = useState<boolean>(() => controlledSnapshot.hasCustomSelection);
  const selectionPath = resolvePath(config.selection);
  const hasCustomSelectionPath = resolvePath(config.hasCustomSelection);
  const updateData = ctx.updateData;
  const title = resolveText(config.title, ctx.data) || DEFAULT_ROLL_PICKER_CARD_TITLE;
  const subTitle = resolveText(config.subTitle, ctx.data) || DEFAULT_ROLL_PICKER_CARD_SUBTITLE;
  const submitText = resolveText(config.submitText, ctx.data) || DEFAULT_ROLL_PICKER_CARD_SUBMIT_TEXT;
  const submittedText = resolveText(config.submittedText, ctx.data) || DEFAULT_ROLL_PICKER_CARD_SUBMITTED_TEXT;

  useEffect(() => {
    setIndexes((current) => (arraysEqual(current, controlledIndexes) ? current : controlledIndexes));
    setHasSubmittedSelection(controlledSnapshot.hasCustomSelection);
  }, [controlledIndexes, controlledSnapshot.hasCustomSelection]);

  const snapshot = useMemo(() => buildRollPickerSnapshot(columns, indexes), [columns, indexes]);
  useEffect(() => {
    if (selectionPath) updateData?.(selectionPath, snapshot.selectedValues);
    if (hasCustomSelectionPath) updateData?.(hasCustomSelectionPath, snapshot.hasCustomSelection);
  }, [hasCustomSelectionPath, selectionPath, snapshot, updateData]);
  const buttonText = hasSubmittedSelection ? submittedText : submitText;
  const actionName = actionNameFromConfig(config);

  if (columns.length === 0) return null;

  return (
    <div className="w-full min-w-[360px] max-w-full rounded-2xl border border-[#DFDDD7] bg-[#FFD1C3] p-3 font-sans shadow-[0_10px_24px_-18px_rgba(80,26,26,0.2)]">
      <div className="text-[20px] font-semibold leading-8 text-[#161615]">{title}</div>
      <div className="text-xs leading-[18px] text-[#595856]">{subTitle}</div>
      <div className="mt-3">
        <RollPickerFrame
          columns={columns}
          indexes={indexes}
          onChange={(columnIndex, nextIndex) => {
            setIndexes((current) => current.map((value, index) => (index === columnIndex ? nextIndex : value)));
          }}
        />
      </div>
      <button
        type="button"
        className="mt-3 inline-flex min-h-[50px] w-full items-center justify-center whitespace-nowrap rounded-full px-4 text-base font-semibold text-[#FFFDF7]"
        style={{ background: `linear-gradient(135deg, ${MACARON.buttonPrimaryStart}, ${MACARON.buttonPrimaryMid}, ${MACARON.buttonPrimaryEnd})` }}
        onClick={() => {
          if (!actionName) return;
          // RollPickerCard actions in Flutter enrich the declared action context with the current picker snapshot.
          ctx.onAction?.(actionName, ctx.componentId, { ...resolveActionContext(config.action as RecordAny | undefined, ctx.data), ...snapshot });
          setHasSubmittedSelection(true);
        }}
      >
        {buttonText}
      </button>
    </div>
  );
}
